import React from "react";
import { Row, Col, Alert } from "reactstrap";
import NeoLoader from "components/Common/NeoLoader";
import useManagerDashboard from "./hooks/useManagerDashboard";
import StatCardAdvance from "../components/StatCardAdvance";
import ActivityChart from "./components/charts/ActivityChart";
import GrowthChart from "./components/charts/GrowthChart";
import RenewalChart from "./components/charts/RenewalChart";
import OccupancyChart from "./components/charts/OccupancyChart";
import AttendanceRateChart from "./components/charts/AttendanceRateChart";
import DatePickerPopover from "./components/DatePickerPopover";
import { useTenant } from "../../../hooks/useTenant";


const ManagerDashboard = () => {
    const { user } = useTenant();
    const { loading, error, data, selectedDate, setSelectedDate, isToday } = useManagerDashboard();

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <NeoLoader type="inline" loading={loading} />
            </div>
        );
    }

    if (error) {
        return (
            <Alert color="danger">
                <i className="mdi mdi-alert-circle-outline me-2"></i>
                Erro ao carregar métricas: {error}
            </Alert>
        );
    }

    // Labels dinâmicos
    const dayLabel = new Date(`${selectedDate}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    const periodLabel = isToday ? "Mês atual" : `Acum. até ${dayLabel}`;
    const compLabel = isToday ? "M. anterior" : "Mesmo período (mês ant.)";

    return (
        <React.Fragment>
            <div className="d-flex justify-content-between align-items-center flex-wrap pt-2 mb-4 gap-3">
                {/* Lado esquerdo - Saudação de Boas-vindas outonal */}
                <div>
                    <h4 className="m-0" style={{ fontFamily: "'Outfit', 'Inter', sans-serif", fontWeight: "900", color: "#482712", letterSpacing: "-0.5px" }}>
                        Olá, {user?.displayName ? user.displayName.split(" ")[0] : "Usuário"}! 👋
                    </h4>
                    <p className="m-0 text-muted" style={{ fontSize: "13px", fontWeight: "500" }}>
                        Seja bem-vindo ao seu painel gerencial.
                    </p>
                </div>

                {/* Lado direito - Data e Seletor */}
                <div className="d-flex align-items-center gap-3">
                    <div className="text-end">
                        <h5 className="m-0" style={{ fontFamily: "'Outfit', 'Inter', sans-serif", fontWeight: "900", color: "#4a2c11", letterSpacing: "0.5px" }}>
                            {new Date(`${selectedDate}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </h5>
                    </div>
                    <DatePickerPopover selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
                </div>
            </div>

            {/* ── Cards Unificados ── */}
            <Row className="align-items-stretch">
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title="Alunos Ativos"
                        currentValue={data.activeMembers}
                        previousValue={data.previousActiveMembers}
                        goalValue={data.goals?.activeMembers || 320}
                        subtitle={isToday ? "Hoje" : `Em ${dayLabel}`}
                        icon="mdi mdi-account-check-outline"
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title="Crescimento Líquido"
                        currentValue={(data.newMembersMonth || 0) - (data.cancellationsMonth || 0)}
                        previousValue={(data.previousNewMembersMonth || 0) - (data.previousCancellationsMonth || 0)}
                        goalValue={data.goals?.crescimentoLiquido || 22}
                        subtitle={`Novos: ${data.newMembersMonth || 0} | Cancel.: ${data.cancellationsMonth || 0}`}
                        icon="mdi mdi-account-star-outline"
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title="Taxa de Renovação"
                        currentValue={
                            (data.expirationsMonth || 0) > 0
                                ? ((data.renewalsMonth || 0) / data.expirationsMonth) * 100
                                : 0
                        }
                        previousValue={
                            (data.previousExpirationsMonth || 0) > 0
                                ? ((data.previousRenewalsMonth || 0) / data.previousExpirationsMonth) * 100
                                : 0
                        }
                        goalValue={data.goals?.renewalRate || 80}
                        subtitle={`${data.renewalsMonth || 0} renovações | ${data.expirationsMonth || 0} vencimentos`}
                        icon="mdi mdi-autorenew"
                        isPercentage={true}
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title="Retornos"
                        currentValue={data.returnsMonth}
                        previousValue={data.previousReturnsMonth}
                        goalValue={data.goals?.returns || 10}
                        subtitle={isToday ? `Hoje: ${data.returnsToday}` : periodLabel}
                        icon="mdi mdi-account-arrow-left-outline"
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title="Cancelamentos"
                        currentValue={data.cancellationsMonth || 0}
                        previousValue={data.previousCancellationsMonth || 0}
                        goalValue={data.goals?.cancellations || 8}
                        isInverse={true}
                        subtitle={periodLabel}
                        icon="mdi mdi-account-cancel-outline"
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title="Churn"
                        currentValue={data.churnRate || 0}
                        previousValue={data.previousChurnRate || 0}
                        goalValue={data.goals?.churnRate || 8}
                        isInverse={true}
                        subtitle="Índice de evasão"
                        icon="mdi mdi-account-alert-outline"
                        isPercentage={true}
                        comparisonLabel={compLabel}
                    />
                </Col>
            </Row>


            <Row className="align-items-stretch mb-4">
                <Col lg={4} md={12} className="mb-4 mb-lg-0">
                    <ActivityChart data={data.activityChartData} />
                </Col>
                <Col lg={4} md={12} className="mb-4 mb-lg-0">
                    <GrowthChart 
                        data={data.growthChartData} 
                        activeMembers={data.activeMembers} 
                        isCurrentYear={Number(selectedDate.slice(0, 4)) === new Date().getFullYear()} 
                    />
                </Col>
                <Col lg={4} md={12}>
                    <RenewalChart data={data.renewalChartData} />
                </Col>
            </Row>

            <Row className="align-items-stretch">
                <Col md={6} className="mb-4">
                    <AttendanceRateChart data={data} chartData={data.attendanceChartData} />
                </Col>
                <Col md={6} className="mb-4">
                    <OccupancyChart data={data.occupancy} />
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default ManagerDashboard;
